<template>
  <v-navigation-drawer
    permanent
    left
    absolute
    width="220"
    class="floor-plan-toolbar"
  >
    <v-list dense>
      <!-- Tool selection -->
      <v-list-item-group v-model="selectedTool" color="#3989C9">
        <v-subheader class="toolbar-subheader">
          {{ $t('$vuetify.floorPlan.tools') }}
          <v-btn icon x-small class="ml-2 help-btn" @click="helpDialog = true">
            <v-icon small color="#3989C9">mdi-help-circle</v-icon>
          </v-btn>
        </v-subheader>

        <!-- Select/Move tool -->
        <v-list-item value="select" @click="selectTool('select')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-cursor-default</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.selectMove') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Table tool -->
        <v-list-item value="add-table" @click="selectTool('add-table')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-table-furniture</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.table') }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon small color="rgba(57, 137, 201, 0.6)">{{ tableShapeExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-list-item-icon>
        </v-list-item>

        <!-- Table shape submenu -->
        <v-expand-transition>
          <div v-show="tableShapeExpanded || selectedTool === 'add-table'" class="pl-8">
            <v-list-item
              value="add-table-circle"
              @click="selectTool('add-table-circle')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-circle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.circle') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              value="add-table-square"
              @click="selectTool('add-table-square')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-square-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.square') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              value="add-table-rectangle"
              @click="selectTool('add-table-rectangle')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-rectangle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.rectangle') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              value="add-table-oval"
              @click="selectTool('add-table-oval')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-ellipse-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.oval') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              value="add-table-corner"
              @click="selectTool('add-table-corner')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-arrow-top-left</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.corner') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-expand-transition>

        <!-- Wall tool -->
        <v-list-item value="add-wall" @click="selectTool('add-wall')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-wall</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.wall') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Door tool -->
        <v-list-item value="add-door" @click="selectTool('add-door')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-door</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.door') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Bar tool -->
        <v-list-item value="add-bar" @click="selectTool('add-bar')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-desk</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.bar') }}</v-list-item-title>
          </v-list-item-content>
          <v-list-item-icon>
            <v-icon small color="rgba(57, 137, 201, 0.6)">{{ barShapeExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-list-item-icon>
        </v-list-item>

        <!-- Bar shape submenu -->
        <v-expand-transition>
          <div v-show="barShapeExpanded || selectedTool === 'add-bar'" class="pl-8">
            <v-list-item
              value="add-bar-rectangle"
              @click="selectTool('add-bar-rectangle')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-rectangle-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.rectangle') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              value="add-bar-semicircle"
              @click="selectTool('add-bar-semicircle')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-circle-half-full</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.semicircle') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <v-list-item
              value="add-bar-semioval"
              @click="selectTool('add-bar-semioval')"
              dense
            >
              <v-list-item-icon>
                <v-icon small color="rgba(57, 137, 201, 0.7)">mdi-ellipse-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-caption">{{ $t('$vuetify.floorPlan.semioval') }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </div>
        </v-expand-transition>

        <!-- Area tool -->
        <v-list-item value="add-area" @click="selectTool('add-area')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-border-all</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.area') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Text tool -->
        <v-list-item value="add-textbox" @click="selectTool('add-textbox')">
          <v-list-item-icon>
            <v-icon color="rgba(57, 137, 201, 0.8)">mdi-text-box</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.textBox') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- Delete tool -->
        <v-list-item value="delete" @click="selectTool('delete')">
          <v-list-item-icon>
            <v-icon color="rgba(244, 67, 54, 0.8)">mdi-delete</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ $t('$vuetify.floorPlan.delete') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list-item-group>

      <v-divider class="my-2"></v-divider>

      <!-- Background -->
      <v-subheader>{{ $t('$vuetify.floorPlan.background') }}</v-subheader>

      <!-- Background pattern -->
      <v-list-item>
        <v-list-item-content>
          <v-select
            :value="backgroundPattern"
            @input="$emit('pattern-changed', $event)"
            :label="$t('$vuetify.floorPlan.pattern')"
            :items="patternOptions"
            dense
            outlined
            hide-details
          >
            <template v-slot:prepend-inner>
              <v-icon small>mdi-texture</v-icon>
            </template>
          </v-select>
        </v-list-item-content>
      </v-list-item>

      <!-- Background color -->
      <v-list-item>
        <v-list-item-content>
          <v-list-item-subtitle class="text-caption mb-1">
            {{ $t('$vuetify.floorPlan.backgroundColor') }}
          </v-list-item-subtitle>
          <v-menu offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                small
                block
                outlined
                v-bind="attrs"
                v-on="on"
              >
                <v-icon left small>mdi-palette</v-icon>
                {{ $t('$vuetify.floorPlan.chooseColor') }}
                <v-spacer></v-spacer>
                <div
                  class="color-preview"
                  :style="{ backgroundColor: backgroundColor }"
                ></div>
              </v-btn>
            </template>
            <v-card>
              <v-color-picker
                :value="backgroundColor"
                @input="$emit('background-color-changed', $event)"
                hide-mode-switch
                mode="hexa"
                flat
              ></v-color-picker>
            </v-card>
          </v-menu>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <!-- View controls -->
      <v-subheader>{{ $t('$vuetify.floorPlan.view') }}</v-subheader>

      <!-- Zoom controls -->
      <v-list-item>
        <v-list-item-content>
          <v-list-item-subtitle class="text-caption mb-1">
            {{ $t('$vuetify.floorPlan.zoom') }}: {{ Math.round(zoom * 100) }}%
          </v-list-item-subtitle>
          <v-slider
            :value="zoom"
            @input="$emit('zoom-changed', $event)"
            @change="$emit('zoom-changed', $event)"
            min="0.3"
            max="3"
            step="0.1"
            hide-details
            dense
          >
            <template v-slot:prepend>
              <v-btn icon x-small @click="$emit('zoom-out')">
                <v-icon>mdi-minus</v-icon>
              </v-btn>
            </template>
            <template v-slot:append>
              <v-btn icon x-small @click="$emit('zoom-in')">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
          </v-slider>
        </v-list-item-content>
      </v-list-item>

      <!-- Reset view button -->
      <v-list-item @click="$emit('reset-view')">
        <v-list-item-icon>
          <v-icon color="rgba(57, 137, 201, 0.8)">mdi-restore</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('$vuetify.floorPlan.resetView') }}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="my-2"></v-divider>

      <!-- Options -->
      <v-subheader>{{ $t('$vuetify.floorPlan.options') }}</v-subheader>

      <!-- Show grid toggle -->
      <v-list-item @click="$emit('toggle-grid')" class="option-list-item">
        <v-list-item-icon>
          <v-icon color="rgba(57, 137, 201, 0.8)">{{ showGrid ? 'mdi-grid' : 'mdi-grid-off' }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('$vuetify.floorPlan.showGrid') }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-switch
            :input-value="showGrid"
            @click.stop="$emit('toggle-grid')"
            color="#3989C9"
            hide-details
            dense
            class="option-switch"
          ></v-switch>
        </v-list-item-action>
      </v-list-item>

      <!-- Snap to grid toggle -->
      <v-list-item @click="$emit('toggle-snap')" class="option-list-item">
        <v-list-item-icon>
          <v-icon color="rgba(57, 137, 201, 0.8)">{{ snapToGrid ? 'mdi-magnet-on' : 'mdi-magnet' }}</v-icon>
        </v-list-item-icon>
        <v-list-item-content>
          <v-list-item-title>{{ $t('$vuetify.floorPlan.snapToGrid') }}</v-list-item-title>
        </v-list-item-content>
        <v-list-item-action>
          <v-switch
            :input-value="snapToGrid"
            @click.stop="$emit('toggle-snap')"
            color="#3989C9"
            hide-details
            dense
            class="option-switch"
          ></v-switch>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <!-- Keyboard shortcuts help -->
    <template v-slot:append>
      <v-divider></v-divider>
      <v-list dense>
        <v-list-item>
          <v-list-item-content>
            <v-list-item-subtitle class="text-caption">
              <strong>{{ $t('$vuetify.floorPlan.shortcuts') }}</strong><br>
              Ctrl+Z: Undo<br>
              Ctrl+Y: Redo<br>
              Ctrl+D: Dupliciraj odabrano<br>
              Delete: {{ $t('$vuetify.floorPlan.deleteSelected') }}<br>
              Esc: {{ $t('$vuetify.floorPlan.deselect') }}
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </template>

    <!-- Help Dialog -->
    <v-dialog v-model="helpDialog" max-width="800px" scrollable>
      <v-card class="help-dialog-card">
        <v-card-title class="help-dialog-title">
          <v-icon left color="white">mdi-help-circle</v-icon>
          <span class="help-dialog-title-text">{{ $t('$vuetify.floorPlan.userManual') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon class="help-close-btn" @click="helpDialog = false">
            <v-icon color="white">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-expansion-panels accordion class="help-expansion-panels">
            <!-- Select/Move Tool -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-cursor-default</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.selectMove') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpSelectMove') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Tables -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-table-furniture</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.table') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpTable') }}</p>
                <v-list dense class="help-list">
                  <v-list-item>
                    <v-list-item-icon><v-icon small color="rgba(57, 137, 201, 0.7)">mdi-circle-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>{{ $t('$vuetify.floorPlan.helpTableCircle') }}</v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon small color="rgba(57, 137, 201, 0.7)">mdi-square-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>{{ $t('$vuetify.floorPlan.helpTableSquare') }}</v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon small color="rgba(57, 137, 201, 0.7)">mdi-rectangle-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>{{ $t('$vuetify.floorPlan.helpTableRectangle') }}</v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon small color="rgba(57, 137, 201, 0.7)">mdi-ellipse-outline</v-icon></v-list-item-icon>
                    <v-list-item-content>{{ $t('$vuetify.floorPlan.helpTableOval') }}</v-list-item-content>
                  </v-list-item>
                  <v-list-item>
                    <v-list-item-icon><v-icon small color="rgba(57, 137, 201, 0.7)">mdi-arrow-top-left</v-icon></v-list-item-icon>
                    <v-list-item-content>{{ $t('$vuetify.floorPlan.helpTableCorner') }}</v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Bars -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-counter</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.bar') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpBar') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Walls -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-wall</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.wall') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpWall') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Areas -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-vector-square</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.area') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpArea') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Doors -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-door</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.door') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpDoor') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Text -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-text</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.textBox') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpText') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Rooms -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-office-building</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.roomsTitle') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpRooms') }}</p>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Background Patterns -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-texture</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.backgroundPatterns') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpPatterns') }}</p>
                <v-list dense class="help-list">
                  <v-list-item><v-list-item-content><strong>{{ $t('$vuetify.floorPlan.patternWood') }}:</strong> {{ $t('$vuetify.floorPlan.helpPatternWood') }}</v-list-item-content></v-list-item>
                  <v-list-item><v-list-item-content><strong>{{ $t('$vuetify.floorPlan.patternParquet') }}:</strong> {{ $t('$vuetify.floorPlan.helpPatternParquet') }}</v-list-item-content></v-list-item>
                  <v-list-item><v-list-item-content><strong>{{ $t('$vuetify.floorPlan.patternTile') }}:</strong> {{ $t('$vuetify.floorPlan.helpPatternTile') }}</v-list-item-content></v-list-item>
                  <v-list-item><v-list-item-content><strong>{{ $t('$vuetify.floorPlan.patternCarpet') }}:</strong> {{ $t('$vuetify.floorPlan.helpPatternCarpet') }}</v-list-item-content></v-list-item>
                  <v-list-item><v-list-item-content><strong>{{ $t('$vuetify.floorPlan.patternMarble') }}:</strong> {{ $t('$vuetify.floorPlan.helpPatternMarble') }}</v-list-item-content></v-list-item>
                  <v-list-item><v-list-item-content><strong>{{ $t('$vuetify.floorPlan.patternConcrete') }}:</strong> {{ $t('$vuetify.floorPlan.helpPatternConcrete') }}</v-list-item-content></v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>

            <!-- Keyboard Shortcuts -->
            <v-expansion-panel>
              <v-expansion-panel-header class="help-panel-header">
                <div class="d-flex align-center">
                  <v-icon left color="rgba(57, 137, 201, 0.8)">mdi-keyboard</v-icon>
                  <strong class="help-panel-title">{{ $t('$vuetify.floorPlan.keyboardShortcuts') }}</strong>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="help-panel-content">
                <p>{{ $t('$vuetify.floorPlan.helpKeyboardShortcuts') }}</p>
                <v-simple-table dense class="mt-2 shortcuts-table">
                  <tbody>
                    <tr><td><kbd>Ctrl+Z</kbd></td><td>Undo — poništi zadnju akciju</td></tr>
                    <tr><td><kbd>Ctrl+Y</kbd></td><td>Redo — ponovi poništenu akciju</td></tr>
                    <tr><td><kbd>Ctrl+D</kbd></td><td>Dupliciraj odabrani objekt</td></tr>
                    <tr><td><kbd>Delete</kbd></td><td>{{ $t('$vuetify.floorPlan.deleteSelected') }}</td></tr>
                    <tr><td><kbd>Esc</kbd></td><td>{{ $t('$vuetify.floorPlan.deselect') }}</td></tr>
                  </tbody>
                </v-simple-table>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn class="help-close-btn" @click="helpDialog = false">
            {{ $t('$vuetify.floorPlan.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-navigation-drawer>
</template>

<script>
export default {
  name: 'FloorPlanToolbar',
  props: {
    activeTool: {
      type: String,
      default: 'select'
    },
    zoom: {
      type: Number,
      default: 1
    },
    snapToGrid: {
      type: Boolean,
      default: true
    },
    showGrid: {
      type: Boolean,
      default: true
    },
    backgroundPattern: {
      type: String,
      default: 'none'
    },
    backgroundColor: {
      type: String,
      default: '#f5f5f5'
    }
  },
  data () {
    return {
      selectedTool: 'select',
      tableShapeExpanded: false,
      barShapeExpanded: false,
      helpDialog: false,
      patternOptions: [
        { text: this.$t('$vuetify.floorPlan.patternNone'), value: 'none' },
        { text: this.$t('$vuetify.floorPlan.patternWood'), value: 'wood' },
        { text: this.$t('$vuetify.floorPlan.patternParquet'), value: 'parquet' },
        { text: this.$t('$vuetify.floorPlan.patternTile'), value: 'tile' },
        { text: this.$t('$vuetify.floorPlan.patternCarpet'), value: 'carpet' },
        { text: this.$t('$vuetify.floorPlan.patternMarble'), value: 'marble' },
        { text: this.$t('$vuetify.floorPlan.patternConcrete'), value: 'concrete' }
      ]
    }
  },
  watch: {
    activeTool: {
      immediate: true,
      handler (newVal) {
        this.selectedTool = newVal
        // Expand table shapes if table tool is selected
        if (newVal && newVal.startsWith('add-table')) {
          this.tableShapeExpanded = true
        }
        // Expand bar shapes if bar tool is selected
        if (newVal && newVal.startsWith('add-bar')) {
          this.barShapeExpanded = true
        }
      }
    }
  },
  methods: {
    selectTool (tool) {
      if (tool === 'add-table') {
        this.tableShapeExpanded = !this.tableShapeExpanded
        if (this.tableShapeExpanded) {
          // Default to circle when expanding
          this.$emit('tool-changed', 'add-table-circle')
        } else {
          // When closing, switch to select tool
          this.$emit('tool-changed', 'select')
        }
      } else if (tool === 'add-bar') {
        this.barShapeExpanded = !this.barShapeExpanded
        if (this.barShapeExpanded) {
          // Default to rectangle when expanding
          this.$emit('tool-changed', 'add-bar-rectangle')
        } else {
          // When closing, switch to select tool
          this.$emit('tool-changed', 'select')
        }
      } else {
        this.$emit('tool-changed', tool)
      }
    }
  }
}
</script>

<style scoped>
/* Floor Plan Toolbar Glass Style */
.floor-plan-toolbar {
  z-index: 5;
  height: 100%;
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-right: 1px solid rgba(0, 179, 190, 0.15) !important;
  box-shadow: 2px 0 12px rgba(0, 179, 190, 0.08) !important;
}

/* Toolbar Subheader */
.toolbar-subheader {
  font-weight: 700 !important;
  font-size: 12px !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  color: rgba(57, 137, 201, 0.9) !important;
  padding-top: 12px !important;
  padding-bottom: 8px !important;
}

/* Help Button */
.help-btn {
  transition: all 0.2s ease !important;
}

.help-btn:hover {
  background: rgba(57, 137, 201, 0.1) !important;
  transform: scale(1.1);
}

/* List Item Styling */
.v-list-item--dense {
  min-height: 40px !important;
  border-radius: 8px !important;
  margin: 2px 8px !important;
  transition: all 0.2s ease !important;
}

.v-list-item:hover {
  background: rgba(57, 137, 201, 0.08) !important;
}

.v-list-item--active {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.12) 0%,
    rgba(57, 137, 201, 0.15) 100%) !important;
  border-left: 3px solid #00B3BE !important;
}

.v-list-item__icon .v-icon {
  color: rgba(57, 137, 201, 0.8) !important;
}

.v-list-item--active .v-list-item__icon .v-icon {
  color: #00B3BE !important;
}

.v-list-item__title {
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
}

/* Subheader Styling */
.v-subheader {
  font-weight: 700 !important;
  font-size: 11px !important;
  text-transform: uppercase !important;
  color: rgba(57, 137, 201, 0.8) !important;
  letter-spacing: 0.5px !important;
  padding-top: 16px !important;
  padding-bottom: 8px !important;
}

/* Color Preview */
.color-preview {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 2px solid rgba(57, 137, 201, 0.3);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
}

.color-preview:hover {
  transform: scale(1.05);
  border-color: rgba(57, 137, 201, 0.5);
}

/* Select and Input Styling */
.v-select >>> .v-input__control {
  background: rgba(255, 255, 255, 0.8) !important;
  border-radius: 8px !important;
}

.v-select >>> fieldset {
  border-color: rgba(57, 137, 201, 0.2) !important;
}

.v-select:hover >>> fieldset {
  border-color: rgba(57, 137, 201, 0.4) !important;
}

/* Slider Styling */
.v-slider >>> .v-slider__thumb {
  background: #3989C9 !important;
  border: 2px solid white !important;
  box-shadow: 0 2px 6px rgba(57, 137, 201, 0.3) !important;
  width: 20px !important;
  height: 20px !important;
}

.v-slider >>> .v-slider__thumb:hover {
  transform: scale(1.1) !important;
}

.v-slider >>> .v-slider__track-fill {
  background: linear-gradient(90deg,
    rgba(0, 179, 190, 0.8) 0%,
    rgba(57, 137, 201, 0.8) 100%) !important;
  border-radius: 4px !important;
}

.v-slider >>> .v-slider__track-background {
  background: rgba(57, 137, 201, 0.15) !important;
  border-radius: 4px !important;
}

.v-slider >>> .v-slider__thumb-label {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.95) 0%,
    rgba(57, 137, 201, 0.95) 100%) !important;
  color: white !important;
  font-weight: 600 !important;
}

/* Button Styling */
.v-btn--outlined {
  border-color: rgba(57, 137, 201, 0.3) !important;
  color: #3989C9 !important;
  border-radius: 8px !important;
  text-transform: none !important;
  font-weight: 500 !important;
  transition: all 0.2s ease !important;
}

.v-btn--outlined:hover {
  background: rgba(57, 137, 201, 0.08) !important;
  border-color: rgba(57, 137, 201, 0.5) !important;
}

/* Switch Styling */
.v-switch >>> .v-input--selection-controls__thumb {
  color: #3989C9 !important;
}

.v-switch >>> .v-input--selection-controls__track {
  color: rgba(57, 137, 201, 0.3) !important;
}

/* Option List Item and Switch */
.option-list-item {
  cursor: pointer !important;
}

.option-switch >>> .v-input--switch__thumb {
  color: white !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2) !important;
}

.option-switch >>> .v-input--switch__track {
  background-color: rgba(120, 120, 130, 0.3) !important;
  opacity: 1 !important;
}

.option-switch.v-input--is-label-active >>> .v-input--switch__track {
  background-color: rgba(57, 137, 201, 0.5) !important;
}

.option-switch >>> .v-input--selection-controls__ripple {
  color: #3989C9 !important;
}

/* Help Dialog Styling */
.help-dialog-card {
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border-radius: 16px !important;
  border: 1px solid rgba(0, 179, 190, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 179, 190, 0.12) !important;
}

.help-dialog-title {
  background: linear-gradient(135deg,
    rgba(0, 179, 190, 0.5) 0%,
    rgba(57, 137, 201, 0.45) 50%,
    rgba(97, 84, 164, 0.5) 100%) !important;
  padding: 24px !important;
  position: relative;
}

.help-dialog-title::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.3;
  pointer-events: none;
}

.help-dialog-title-text {
  color: white !important;
  font-weight: 700 !important;
  font-size: 20px !important;
  letter-spacing: 0.5px !important;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
}

.help-expansion-panels >>> .v-expansion-panel {
  background: rgba(255, 255, 255, 0.6) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 8px !important;
  margin-bottom: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.1) !important;
  overflow: hidden;
}

.help-expansion-panels >>> .v-expansion-panel:hover {
  border-color: rgba(57, 137, 201, 0.2) !important;
}

.help-expansion-panels >>> .v-expansion-panel::before {
  box-shadow: none !important;
}

.help-panel-header {
  padding: 16px !important;
  min-height: 56px !important;
}

.help-panel-header >>> .v-expansion-panel-header__icon .v-icon {
  color: rgba(57, 137, 201, 0.6) !important;
  font-size: 20px !important;
}

.help-panel-title {
  color: rgba(70, 70, 80, 0.95) !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
}

.help-panel-content {
  padding: 16px 16px 20px 16px !important;
  background: rgba(255, 255, 255, 0.4) !important;
}

.help-list {
  padding-left: 0 !important;
}

.help-list >>> .v-list-item {
  padding-left: 12px !important;
  min-height: 36px !important;
}

.help-list >>> .v-list-item:hover {
  background: rgba(57, 137, 201, 0.05) !important;
}

.help-list >>> .v-list-item__icon {
  margin-right: 12px !important;
}

.help-list >>> .v-list-item__title {
  font-size: 14px !important;
  color: rgba(70, 70, 80, 0.9) !important;
  line-height: 1.5 !important;
}

.help-close-btn {
  background: rgba(255, 255, 255, 0.8) !important;
  color: #3989C9 !important;
  border-radius: 8px !important;
  border: 1px solid rgba(57, 137, 201, 0.15) !important;
  box-shadow: 0 2px 8px rgba(57, 137, 201, 0.15) !important;
  backdrop-filter: blur(10px) !important;
  text-transform: none !important;
  font-weight: 600 !important;
  letter-spacing: 0.3px !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.help-close-btn:hover {
  box-shadow: 0 4px 12px rgba(57, 137, 201, 0.25) !important;
  border-color: rgba(57, 137, 201, 0.3) !important;
  transform: translateY(-1px);
}

kbd {
  background: rgba(57, 137, 201, 0.1);
  border: 1px solid rgba(57, 137, 201, 0.25);
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 12px;
  font-family: monospace;
  color: #3989C9;
  white-space: nowrap;
}

.shortcuts-table td {
  padding: 4px 8px !important;
  font-size: 13px;
}

.shortcuts-table td:first-child {
  width: 110px;
}
</style>
